export function commonVideoRange(screenDuration, cameraDuration, offset) {
    if (![screenDuration, cameraDuration, offset].every(Number.isFinite)) return null;
    const start = Math.max(0, -offset);
    const end = Math.min(screenDuration, cameraDuration - offset);
    return end > start ? { start, end } : null;
}

// offset: cameraTime = screenTime + offset. Keep all operations in file
// seconds; date/timezones never enter the playback clock.
export function synchronizePlayers(screen, camera, offset, onError = () => {}) {
    const players = [screen, camera];
    const expectedSeek = new Map();
    const expectedState = new Map();
    const listeners = [];
    let leader = screen;
    let resumeAfterBuffering = false;
    let holding = false;
    const buffering = new Set();

    function hold() {
        resumeAfterBuffering = resumeAfterBuffering || players.some(player => !player.paused());
        holding = true;
        players.forEach(player => setState(player, false));
    }

    function resume() {
        if (!holding || buffering.size || players.some(player =>
            (player.seeking && player.seeking()) || (player.readyState && player.readyState() < 3))) return;
        holding = false;
        if (resumeAfterBuffering) {
            resumeAfterBuffering = false;
            players.forEach(player => setState(player, true));
        }
    }

    function seek(player, time) {
        if (Math.abs(player.currentTime() - time) < 0.08) return;
        expectedSeek.set(player, time);
        player.currentTime(time);
    }

    function align(source = leader) {
        const range = commonVideoRange(screen.duration(), camera.duration(), offset);
        if (!range) return false;
        const screenTime = source === camera ? source.currentTime() - offset : source.currentTime();
        const time = Math.max(range.start, Math.min(range.end, screenTime));
        seek(screen, time);
        seek(camera, time + offset);
        return true;
    }

    function setState(player, playing) {
        if (player.paused() === !playing) return;
        expectedState.set(player, playing);
        if (playing) {
            const promise = player.play();
            if (promise && promise.catch) promise.catch(() => {
                expectedState.delete(player);
                screen.pause(); camera.pause();
                onError('Не удалось запустить оба видео. Нажмите воспроизведение ещё раз.');
            });
        } else player.pause();
    }

    players.forEach(source => {
        const target = source === screen ? camera : screen;
        const handlers = {
            waiting: () => { buffering.add(source); hold(); },
            canplay: () => { buffering.delete(source); resume(); },
            seeked: () => { buffering.delete(source); resume(); },
            seeking: () => {
                if (source.seeking && source.seeking()) hold();
                if (expectedSeek.has(source) && Math.abs(source.currentTime() - expectedSeek.get(source)) < 0.2) {
                    expectedSeek.delete(source);
                    return;
                }
                expectedSeek.delete(source);
                leader = source;
                align(source);
            },
            play: () => {
                if (expectedState.get(source) === true) { expectedState.delete(source); return; }
                leader = source;
                if (holding) { resumeAfterBuffering = true; hold(); resume(); return; }
                if (align(source)) setState(target, true);
                else if (source.readyState && target.readyState) hold();
            },
            pause: () => {
                if (expectedState.get(source) === false) { expectedState.delete(source); return; }
                resumeAfterBuffering = false;
                setState(target, false);
            },
            ratechange: () => {
                if (target.playbackRate() !== source.playbackRate()) target.playbackRate(source.playbackRate());
            },
            timeupdate: () => {
                if (source === leader && !source.paused()) {
                    const range = commonVideoRange(screen.duration(), camera.duration(), offset);
                    const screenTime = source.currentTime() - (source === camera ? offset : 0);
                    if (range && screenTime >= range.end) {
                        screen.pause(); camera.pause();
                        align(source);
                        return;
                    }
                    const expected = source.currentTime() + (source === screen ? offset : -offset);
                    if (Math.abs(target.currentTime() - expected) > 0.35) align(source);
                }
            },
            loadedmetadata: () => {
                if (align()) {
                    if (holding) resume();
                    else setState(leader === screen ? camera : screen, !leader.paused());
                }
            },
            ended: () => { screen.pause(); camera.pause(); }
        };
        Object.entries(handlers).forEach(([event, handler]) => {
            source.on(event, handler);
            listeners.push([source, event, handler]);
        });
    });
    align();
    return {
        seek(source, time) { leader = source; seek(source, Math.max(0, time)); align(source); },
        dispose() { listeners.forEach(([player, event, handler]) => player.off(event, handler)); }
    };
}
